import {
    EdgeChange,
    MarkerType,
    NodeChange,
    Position,
    ReactFlowInstance,
    Viewport,
    applyEdgeChanges,
    applyNodeChanges,
} from '@xyflow/react'
import { deepEqual as equal } from 'fast-equals'
import { MakeLogicType, actions, afterMount, beforeUnmount, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { urlToAction } from 'kea-router'
import type { RefObject } from 'react'

import api from 'lib/api'
import { urls } from 'scenes/urls'

import {
    DataModelingDAG,
    DataModelingEdge,
    DataModelingJob,
    DataModelingJobStatus,
    DataModelingNode,
    DataModelingNodeType,
} from '~/types'

import { getFormattedNodes } from './modeling/autolayout'
import { PAGE_SIZE } from './modeling/constants'
import { Edge, ElkDirection, Node, NodeHandle, SearchMode, ViewMode } from './modeling/types'

const POLL_INTERVAL_MS = 5000
const MIN_RUNNING_DURATION_MS = 2000

const nodeStartTimes: Map<string, number> = new Map()

const getEdgeId = (from: string, to: string): string => `${from}->${to}`

interface AdjacencyMaps {
    upstream: Map<string, string[]>
    downstream: Map<string, string[]>
}

function buildAdjacencyMaps(edges: Edge[]): AdjacencyMaps {
    const upstream = new Map<string, string[]>()
    const downstream = new Map<string, string[]>()
    for (const edge of edges) {
        if (!upstream.has(edge.target)) {
            upstream.set(edge.target, [])
        }
        upstream.get(edge.target)!.push(edge.source)
        if (!downstream.has(edge.source)) {
            downstream.set(edge.source, [])
        }
        downstream.get(edge.source)!.push(edge.target)
    }
    return { upstream, downstream }
}

function traverseGraph(startId: string, adjacencyMap: Map<string, string[]>): Set<string> {
    const result = new Set<string>()
    const queue = [startId]
    while (queue.length > 0) {
        const current = queue.shift()!
        const neighbors = adjacencyMap.get(current) ?? []
        for (const neighbor of neighbors) {
            if (!result.has(neighbor)) {
                result.add(neighbor)
                queue.push(neighbor)
            }
        }
    }
    return result
}

export interface ParsedSearch {
    mode: SearchMode
    baseName: string
}

/** Parse search term for +name (upstream), name+ (downstream), or +name+ (both) syntax */
export function parseSearchTerm(searchTerm: string): ParsedSearch {
    const trimmed = searchTerm.trim()
    if (trimmed.startsWith('+') && trimmed.endsWith('+') && trimmed.length > 2) {
        return { mode: 'all', baseName: trimmed.slice(1, -1) }
    }
    if (trimmed.startsWith('+') && trimmed.length > 1) {
        return { mode: 'upstream', baseName: trimmed.slice(1) }
    }
    if (trimmed.endsWith('+') && trimmed.length > 1) {
        return { mode: 'downstream', baseName: trimmed.slice(0, -1) }
    }
    return { mode: 'search', baseName: trimmed }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataModelingLogicValues {
    availableDagIds: string[]
    availableTypes: DataModelingNodeType[]
    currentPage: number
    dags: DataModelingDAG[]
    dagsLoading: boolean
    dataModelingEdges: DataModelingEdge[]
    dataModelingEdgesLoading: boolean
    dataModelingNodes: DataModelingNode[]
    dataModelingNodesLoading: boolean
    debouncedSearchTerm: string
    edges: Edge[]
    enrichedEdges: Edge[]
    enrichedNodes: Node[]
    filterDagIds: string[]
    filterTypes: DataModelingNodeType[]
    filteredNodes: DataModelingNode[]
    graphReady: boolean
    highlightedNodeIds: (baseName: string, mode: 'all' | 'downstream' | 'upstream') => Set<string>
    highlightedNodeType: DataModelingNodeType | null
    hoveredNodeId: string | null
    latestJobMetadataByNodeId: Record<
        string,
        {
            lastRunAt: string | null
            status: DataModelingJobStatus
        }
    >
    latestJobMetadataBySavedQueryId: Record<
        string,
        {
            lastRunAt: string | null
            status: DataModelingJobStatus
        }
    >
    layoutDirection: ElkDirection
    nodeTypePanelCollapsed: boolean
    nodes: Node[]
    nodesById: Record<string, Node>
    nodesLoading: boolean
    parsedSearch: ParsedSearch
    reactFlowInstance: ReactFlowInstance<Node, Edge> | null
    reactFlowWrapper: RefObject<HTMLDivElement> | null
    runningNodeIds: Set<string>
    savedViewport: Viewport | null
    searchLayoutNodes: Node[] | null
    searchMatchedNodeIds: Set<string> | null
    searchTerm: string
    selectedDag: DataModelingDAG | null
    selectedDagId: string | null
    viewMode: ViewMode
    viewNodes: DataModelingNode[]
    visibleNodes: DataModelingNode[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataModelingLogicActions {
    clearFilterDagIds: () => {
        value: true
    }
    clearFilterTypes: () => {
        value: true
    }
    layoutSearchResults: () => {
        value: true
    }
    loadDags: () => any
    loadDagsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDagsSuccess: (
        dags: DataModelingDAG[],
        payload?: any
    ) => {
        dags: DataModelingDAG[]
        payload?: any
    }
    loadDataModelingEdges: () => any
    loadDataModelingEdgesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDataModelingEdgesSuccess: (
        dataModelingEdges: DataModelingEdge[],
        payload?: any
    ) => {
        dataModelingEdges: DataModelingEdge[]
        payload?: any
    }
    loadDataModelingNodes: () => any
    loadDataModelingNodesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDataModelingNodesSuccess: (
        dataModelingNodes: DataModelingNode[],
        payload?: any
    ) => {
        dataModelingNodes: DataModelingNode[]
        payload?: any
    }
    loadRecentJobs: () => {
        value: true
    }
    loadRecentJobsSuccess: (recentJobs: DataModelingJob[]) => {
        recentJobs: DataModelingJob[]
    }
    materializeNode: (nodeId: string) => {
        nodeId: string
    }
    materializeNodeFailure: (
        nodeId: string,
        error: string
    ) => {
        error: string
        nodeId: string
    }
    materializeNodeSuccess: (nodeId: string) => {
        nodeId: string
    }
    onEdgesChange: (edges: EdgeChange<Edge>[]) => {
        edges: EdgeChange<Edge>[]
    }
    onNodesChange: (nodes: NodeChange<Node>[]) => {
        nodes: NodeChange<Node>[]
    }
    pollRunningJobs: () => {
        value: true
    }
    pollRunningJobsSuccess: (runningJobs: DataModelingJob[]) => {
        runningJobs: DataModelingJob[]
    }
    resetGraph: (
        dataModelingNodes: DataModelingNode[],
        dataModelingEdges: DataModelingEdge[],
        fitViewAfter?: boolean
    ) => {
        dataModelingEdges: DataModelingEdge[]
        dataModelingNodes: DataModelingNode[]
        fitViewAfter: boolean | undefined
    }
    runNode: (
        nodeId: string,
        direction: 'downstream' | 'upstream'
    ) => {
        direction: 'downstream' | 'upstream'
        nodeId: string
    }
    runNodeFailure: (
        nodeId: string,
        direction: 'downstream' | 'upstream',
        error: string
    ) => {
        direction: 'downstream' | 'upstream'
        error: string
        nodeId: string
    }
    runNodeSuccess: (
        nodeId: string,
        direction: 'downstream' | 'upstream',
        runningNodeIds: string[]
    ) => {
        direction: 'downstream' | 'upstream'
        nodeId: string
        runningNodeIds: string[]
    }
    setCurrentPage: (page: number) => {
        page: number
    }
    setDebouncedSearchTerm: (debouncedSearchTerm: string) => {
        debouncedSearchTerm: string
    }
    setEdges: (edges: Edge[]) => {
        edges: Edge[]
    }
    setGraphReady: (ready: boolean) => {
        ready: boolean
    }
    setHighlightedNodeType: (highlightedNodeType: DataModelingNodeType | null) => {
        highlightedNodeType: DataModelingNodeType | null
    }
    setHoveredNodeId: (hoveredNodeId: string | null) => {
        hoveredNodeId: string | null
    }
    setLayoutDirection: (layoutDirection: ElkDirection) => {
        layoutDirection: ElkDirection
    }
    setNodeTypePanelCollapsed: (collapsed: boolean) => {
        collapsed: boolean
    }
    setNodes: (
        nodes: Node[],
        fitViewAfter?: boolean
    ) => {
        fitViewAfter: boolean | undefined
        nodes: Node[]
    }
    setNodesRaw: (nodes: Node[]) => {
        nodes: Node[]
    }
    setReactFlowInstance: (reactFlowInstance: ReactFlowInstance<Node, Edge>) => {
        reactFlowInstance: ReactFlowInstance<Node, Edge>
    }
    setReactFlowWrapper: (reactFlowWrapper: RefObject<HTMLDivElement>) => {
        reactFlowWrapper: RefObject<HTMLDivElement>
    }
    setRunningNodeIds: (runningNodeIds: Set<string>) => {
        runningNodeIds: Set<string>
    }
    setSavedViewport: (viewport: Viewport) => {
        viewport: Viewport
    }
    setSearchLayoutNodes: (nodes: Node[] | null) => {
        nodes: Node[] | null
    }
    setSearchTerm: (searchTerm: string) => {
        searchTerm: string
    }
    setSelectedDagId: (dagId: string | null) => {
        dagId: string | null
    }
    setViewMode: (viewMode: ViewMode) => {
        viewMode: ViewMode
    }
    startPollingRunningJobs: () => {
        value: true
    }
    stopPollingRunningJobs: () => {
        value: true
    }
    toggleFilterDagId: (dagId: string) => {
        dagId: string
    }
    toggleFilterType: (nodeType: DataModelingNodeType) => {
        nodeType: DataModelingNodeType
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataModelingLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        nodesById: (nodes: Node[]) => Record<string, Node>
        nodesLoading: (
            dataModelingNodesLoading: boolean,
            dataModelingEdgesLoading: boolean,
            graphReady: boolean,
            viewMode: ViewMode
        ) => boolean
        latestJobMetadataByNodeId: (
            nodes: Node[],
            latestJobMetadataBySavedQueryId: Record<
                string,
                {
                    lastRunAt: string | null
                    status: DataModelingJobStatus
                }
            >
        ) => Record<
            string,
            {
                lastRunAt: string | null
                status: DataModelingJobStatus
            }
        >
        enrichedNodes: (
            nodes: Node[],
            searchLayoutNodes: Node[] | null,
            runningNodeIds: Set<string>,
            highlightedNodeType: DataModelingNodeType | null,
            latestJobMetadataByNodeId: Record<
                string,
                {
                    lastRunAt: string | null
                    status: DataModelingJobStatus
                }
            >,
            searchMatchedNodeIds: Set<string> | null
        ) => Node[]
        enrichedEdges: (edges: Edge[], hoveredNodeId: string | null, searchMatchedNodeIds: Set<string> | null) => Edge[]
        highlightedNodeIds: (
            nodes: Node[],
            edges: Edge[]
        ) => (baseName: string, mode: 'all' | 'downstream' | 'upstream') => Set<string>
        parsedSearch: (debouncedSearchTerm: string) => ParsedSearch
        searchMatchedNodeIds: (
            nodes: Node[],
            debouncedSearchTerm: string,
            parsedSearch: ParsedSearch,
            highlightedNodeIds: (baseName: string, mode: 'all' | 'downstream' | 'upstream') => Set<string>
        ) => Set<string> | null
        filteredNodes: (dataModelingNodes: DataModelingNode[], searchTerm: string) => DataModelingNode[]
        selectedDag: (dags: DataModelingDAG[], selectedDagId: string | null) => DataModelingDAG | null
        availableDagIds: (filteredNodes: DataModelingNode[]) => string[]
        availableTypes: (filteredNodes: DataModelingNode[]) => DataModelingNodeType[]
        viewNodes: (
            filteredNodes: DataModelingNode[],
            filterDagIds: string[],
            filterTypes: DataModelingNodeType[]
        ) => DataModelingNode[]
        visibleNodes: (viewNodes: DataModelingNode[], currentPage: number) => DataModelingNode[]
    }
}

export type dataModelingLogicType = MakeLogicType<
    dataModelingLogicValues,
    dataModelingLogicActions,
    Record<string, any>,
    dataModelingLogicMeta
>

export const dataModelingLogic = kea<dataModelingLogicType>([
    path(['scenes', 'data-warehouse', 'scene', 'dataModelingLogic']),
    actions({
        onEdgesChange: (edges: EdgeChange<Edge>[]) => ({ edges }),
        onNodesChange: (nodes: NodeChange<Node>[]) => ({ nodes }),
        setNodes: (nodes: Node[], fitViewAfter?: boolean) => ({ nodes, fitViewAfter }),
        setNodesRaw: (nodes: Node[]) => ({ nodes }),
        setSearchLayoutNodes: (nodes: Node[] | null) => ({ nodes }),
        setEdges: (edges: Edge[]) => ({ edges }),
        layoutSearchResults: true,
        setReactFlowInstance: (reactFlowInstance: ReactFlowInstance<Node, Edge>) => ({
            reactFlowInstance,
        }),
        setReactFlowWrapper: (reactFlowWrapper: RefObject<HTMLDivElement>) => ({ reactFlowWrapper }),
        setHighlightedNodeType: (highlightedNodeType: DataModelingNodeType | null) => ({ highlightedNodeType }),
        setHoveredNodeId: (hoveredNodeId: string | null) => ({ hoveredNodeId }),
        setSavedViewport: (viewport: Viewport) => ({ viewport }),
        resetGraph: (
            dataModelingNodes: DataModelingNode[],
            dataModelingEdges: DataModelingEdge[],
            fitViewAfter?: boolean
        ) => ({
            dataModelingNodes,
            dataModelingEdges,
            fitViewAfter,
        }),
        runNode: (nodeId: string, direction: 'upstream' | 'downstream') => ({ nodeId, direction }),
        runNodeSuccess: (nodeId: string, direction: 'upstream' | 'downstream', runningNodeIds: string[]) => ({
            nodeId,
            direction,
            runningNodeIds,
        }),
        runNodeFailure: (nodeId: string, direction: 'upstream' | 'downstream', error: string) => ({
            nodeId,
            direction,
            error,
        }),
        materializeNode: (nodeId: string) => ({ nodeId }),
        materializeNodeSuccess: (nodeId: string) => ({ nodeId }),
        materializeNodeFailure: (nodeId: string, error: string) => ({ nodeId, error }),
        setRunningNodeIds: (runningNodeIds: Set<string>) => ({ runningNodeIds }),
        startPollingRunningJobs: true,
        stopPollingRunningJobs: true,
        pollRunningJobs: true,
        pollRunningJobsSuccess: (runningJobs: DataModelingJob[]) => ({ runningJobs }),
        loadRecentJobs: true,
        loadRecentJobsSuccess: (recentJobs: DataModelingJob[]) => ({ recentJobs }),
        setLayoutDirection: (layoutDirection: ElkDirection) => ({ layoutDirection }),
        // View/filter actions (previously in dataModelingNodesLogic)
        setViewMode: (viewMode: ViewMode) => ({ viewMode }),
        setSearchTerm: (searchTerm: string) => ({ searchTerm }),
        setDebouncedSearchTerm: (debouncedSearchTerm: string) => ({ debouncedSearchTerm }),
        setCurrentPage: (page: number) => ({ page }),
        toggleFilterDagId: (dagId: string) => ({ dagId }),
        clearFilterDagIds: true,
        toggleFilterType: (nodeType: DataModelingNodeType) => ({ nodeType }),
        clearFilterTypes: true,
        setNodeTypePanelCollapsed: (collapsed: boolean) => ({ collapsed }),
        setSelectedDagId: (dagId: string | null) => ({ dagId }),
        setGraphReady: (ready: boolean) => ({ ready }),
    }),
    loaders(({ values }) => ({
        dags: [
            [] as DataModelingDAG[],
            {
                loadDags: async () => {
                    const response = await api.dataModelingDags.list()
                    return response.results
                },
            },
        ],
        dataModelingNodes: [
            [] as DataModelingNode[],
            {
                loadDataModelingNodes: async () => {
                    const dagId = values.selectedDagId ?? undefined
                    const response = await api.dataModelingNodes.list(dagId)
                    return response.results
                },
            },
        ],
        dataModelingEdges: [
            [] as DataModelingEdge[],
            {
                loadDataModelingEdges: async () => {
                    const dagId = values.selectedDagId ?? undefined
                    const response = await api.dataModelingEdges.list(dagId)
                    return response.results
                },
            },
        ],
    })),
    reducers(() => ({
        nodes: [
            [] as Node[],
            {
                setNodesRaw: (_, { nodes }) => nodes,
            },
        ],
        searchLayoutNodes: [
            null as Node[] | null,
            {
                setSearchLayoutNodes: (_, { nodes }) => nodes,
                setDebouncedSearchTerm: () => null,
                resetGraph: () => null,
            },
        ],
        highlightedNodeType: [
            null as DataModelingNodeType | null,
            {
                setHighlightedNodeType: (_, { highlightedNodeType }) => highlightedNodeType,
            },
        ],
        hoveredNodeId: [
            null as string | null,
            {
                setHoveredNodeId: (_, { hoveredNodeId }) => hoveredNodeId,
            },
        ],
        savedViewport: [
            null as Viewport | null,
            {
                setSavedViewport: (_, { viewport }) => viewport,
            },
        ],
        edges: [
            [] as Edge[],
            {
                setEdges: (_, { edges }) => edges,
            },
        ],
        reactFlowInstance: [
            null as ReactFlowInstance<Node, Edge> | null,
            {
                setReactFlowInstance: (_, { reactFlowInstance }) => reactFlowInstance,
            },
        ],
        reactFlowWrapper: [
            null as RefObject<HTMLDivElement> | null,
            {
                setReactFlowWrapper: (_, { reactFlowWrapper }) => reactFlowWrapper,
            },
        ],
        runningNodeIds: [
            new Set<string>(),
            {
                setRunningNodeIds: (_, { runningNodeIds }) => {
                    for (const nodeId of nodeStartTimes.keys()) {
                        if (!runningNodeIds.has(nodeId)) {
                            nodeStartTimes.delete(nodeId)
                        }
                    }
                    return runningNodeIds
                },
                runNodeSuccess: (state, { runningNodeIds }) => {
                    const now = Date.now()
                    for (const nodeId of runningNodeIds) {
                        if (!nodeStartTimes.has(nodeId)) {
                            nodeStartTimes.set(nodeId, now)
                        }
                    }
                    return new Set([...state, ...runningNodeIds])
                },
                materializeNode: (state, { nodeId }) => {
                    if (!nodeStartTimes.has(nodeId)) {
                        nodeStartTimes.set(nodeId, Date.now())
                    }
                    return new Set([...state, nodeId])
                },
            },
        ],
        layoutDirection: [
            'RIGHT' as ElkDirection,
            { persist: true },
            {
                setLayoutDirection: (_, { layoutDirection }) => layoutDirection,
            },
        ],
        nodeTypePanelCollapsed: [
            false,
            { persist: true },
            {
                setNodeTypePanelCollapsed: (_, { collapsed }) => collapsed,
            },
        ],
        selectedDagId: [
            null as string | null,
            { persist: true },
            {
                setSelectedDagId: (_, { dagId }) => dagId,
            },
        ],
        graphReady: [
            false as boolean,
            {
                setGraphReady: (_, { ready }) => ready,
                // reset when DAG changes so spinner shows immediately
                setSelectedDagId: () => false,
                // reset on initial load
                loadDataModelingNodes: () => false,
            },
        ],
        viewMode: [
            'graph' as ViewMode,
            { persist: true },
            {
                setViewMode: (_, { viewMode }) => viewMode,
            },
        ],
        searchTerm: [
            '' as string,
            {
                setSearchTerm: (_, { searchTerm }) => searchTerm,
            },
        ],
        debouncedSearchTerm: [
            '' as string,
            {
                setDebouncedSearchTerm: (_, { debouncedSearchTerm }) => debouncedSearchTerm,
            },
        ],
        currentPage: [
            1 as number,
            {
                setCurrentPage: (_, { page }) => page,
                setSearchTerm: () => 1,
                toggleFilterDagId: () => 1,
                clearFilterDagIds: () => 1,
                toggleFilterType: () => 1,
                clearFilterTypes: () => 1,
            },
        ],
        filterDagIds: [
            [] as string[],
            {
                toggleFilterDagId: (state, { dagId }) =>
                    state.includes(dagId) ? state.filter((id) => id !== dagId) : [...state, dagId],
                clearFilterDagIds: () => [],
            },
        ],
        filterTypes: [
            [] as DataModelingNodeType[],
            {
                toggleFilterType: (state, { nodeType }) =>
                    state.includes(nodeType) ? state.filter((t) => t !== nodeType) : [...state, nodeType],
                clearFilterTypes: () => [],
            },
        ],
        latestJobMetadataBySavedQueryId: [
            {} as Record<string, { status: DataModelingJobStatus; lastRunAt: string | null }>,
            {
                loadRecentJobsSuccess: (state, { recentJobs }) => {
                    const map: Record<string, { status: DataModelingJobStatus; lastRunAt: string | null }> = {}
                    for (const job of recentJobs) {
                        if (job.saved_query_id) {
                            map[job.saved_query_id] = {
                                status: job.status,
                                lastRunAt: job.last_run_at,
                            }
                        }
                    }
                    return equal(state, map) ? state : map
                },
            },
        ],
    })),
    selectors({
        nodesById: [
            (s) => [s.nodes],
            (nodes: Node[]): Record<string, Node> => {
                return nodes.reduce(
                    (acc, node) => {
                        acc[node.id] = node
                        return acc
                    },
                    {} as Record<string, Node>
                )
            },
            { resultEqualityCheck: equal },
        ],
        nodesLoading: [
            (s) => [s.dataModelingNodesLoading, s.dataModelingEdgesLoading, s.graphReady, s.viewMode],
            (
                dataModelingNodesLoading: boolean,
                dataModelingEdgesLoading: boolean,
                graphReady: boolean,
                viewMode: ViewMode
            ): boolean => dataModelingNodesLoading || dataModelingEdgesLoading || (viewMode === 'graph' && !graphReady),
        ],
        latestJobMetadataByNodeId: [
            (s) => [s.nodes, s.latestJobMetadataBySavedQueryId],
            (
                nodes: Node[],
                latestJobMetadataBySavedQueryId: Record<
                    string,
                    {
                        lastRunAt: string | null
                        status: DataModelingJobStatus
                    }
                >
            ): Record<string, { status: DataModelingJobStatus; lastRunAt: string | null }> => {
                const map: Record<string, { status: DataModelingJobStatus; lastRunAt: string | null }> = {}
                for (const node of nodes) {
                    if (node.data.savedQueryId && latestJobMetadataBySavedQueryId[node.data.savedQueryId]) {
                        map[node.id] = latestJobMetadataBySavedQueryId[node.data.savedQueryId]
                    }
                }
                return map
            },
            { resultEqualityCheck: equal },
        ],
        enrichedNodes: [
            (s) => [
                s.nodes,
                s.searchLayoutNodes,
                s.runningNodeIds,
                s.highlightedNodeType,
                s.latestJobMetadataByNodeId,
                s.searchMatchedNodeIds,
            ],
            (
                nodes: Node[],
                searchLayoutNodes: Node[] | null,
                runningNodeIds: Set<string>,
                highlightedNodeType: DataModelingNodeType | null,
                latestJobMetadataByNodeId: Record<
                    string,
                    {
                        lastRunAt: string | null
                        status: DataModelingJobStatus
                    }
                >,
                searchMatchedNodeIds: Set<string> | null
            ): Node[] => {
                const layoutNodes = searchLayoutNodes ?? nodes
                const visibleNodes = searchMatchedNodeIds
                    ? layoutNodes.filter((node) => searchMatchedNodeIds.has(node.id))
                    : layoutNodes
                return visibleNodes.map((node) => {
                    const isRunning = runningNodeIds.has(node.id)
                    const isTypeHighlighted = highlightedNodeType !== null && highlightedNodeType === node.data.type
                    const isSearchMatch = searchMatchedNodeIds !== null
                    const metadata = latestJobMetadataByNodeId[node.id]
                    const lastJobStatus = metadata?.status ?? node.data.lastJobStatus
                    const lastRunAt = metadata?.lastRunAt ?? node.data.lastRunAt
                    if (
                        node.data.isRunning === isRunning &&
                        node.data.isTypeHighlighted === isTypeHighlighted &&
                        node.data.isSearchMatch === isSearchMatch &&
                        node.data.lastJobStatus === lastJobStatus &&
                        node.data.lastRunAt === lastRunAt
                    ) {
                        return node
                    }
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            isRunning,
                            isTypeHighlighted,
                            isSearchMatch,
                            lastJobStatus,
                            lastRunAt,
                        },
                    }
                })
            },
            {
                resultEqualityCheck: (a: Node[], b: Node[]): boolean =>
                    a.length === b.length && a.every((node, i) => node === b[i]),
            },
        ],
        enrichedEdges: [
            (s) => [s.edges, s.hoveredNodeId, s.searchMatchedNodeIds],
            (edges: Edge[], hoveredNodeId: string | null, searchMatchedNodeIds: Set<string> | null): Edge[] => {
                const visibleEdges = searchMatchedNodeIds
                    ? edges.filter(
                          (edge) => searchMatchedNodeIds.has(edge.source) && searchMatchedNodeIds.has(edge.target)
                      )
                    : edges
                if (!hoveredNodeId) {
                    return visibleEdges
                }
                return visibleEdges.map((edge) => {
                    const isConnected = edge.source === hoveredNodeId || edge.target === hoveredNodeId
                    if (!isConnected) {
                        return edge
                    }
                    return {
                        ...edge,
                        style: { stroke: 'var(--primary-3000)' },
                        markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--primary-3000)' },
                    }
                })
            },
            {
                resultEqualityCheck: (a: Edge[], b: Edge[]): boolean =>
                    a.length === b.length && a.every((edge, i) => edge === b[i]),
            },
        ],
        highlightedNodeIds: [
            (s) => [s.nodes, s.edges],
            (
                nodes: Node[],
                edges: Edge[]
            ): ((baseName: string, mode: 'upstream' | 'downstream' | 'all') => Set<string>) => {
                const { upstream, downstream } = buildAdjacencyMaps(edges)

                return (baseName: string, mode: 'upstream' | 'downstream' | 'all'): Set<string> => {
                    const lowerBaseName = baseName.toLowerCase()
                    let startNode = nodes.find((n) => n.data.name.toLowerCase() === lowerBaseName)
                    if (!startNode) {
                        startNode = nodes.find((n) => n.data.name.toLowerCase().includes(lowerBaseName))
                    }
                    if (!startNode) {
                        return new Set()
                    }

                    const result = new Set<string>([startNode.id])

                    if (mode === 'upstream' || mode === 'all') {
                        for (const id of traverseGraph(startNode.id, upstream)) {
                            result.add(id)
                        }
                    }
                    if (mode === 'downstream' || mode === 'all') {
                        for (const id of traverseGraph(startNode.id, downstream)) {
                            result.add(id)
                        }
                    }

                    return result
                }
            },
        ],
        // View/filter selectors (previously in dataModelingNodesLogic)
        parsedSearch: [
            (s) => [s.debouncedSearchTerm],
            (debouncedSearchTerm: string): ParsedSearch => parseSearchTerm(debouncedSearchTerm),
        ],
        /** Node ids to show on the graph canvas for the active search — null means no filter (show everything) */
        searchMatchedNodeIds: [
            (s) => [s.nodes, s.debouncedSearchTerm, s.parsedSearch, s.highlightedNodeIds],
            (
                nodes: Node[],
                debouncedSearchTerm: string,
                parsedSearch: ParsedSearch,
                highlightedNodeIds: (baseName: string, mode: 'upstream' | 'downstream' | 'all') => Set<string>
            ): Set<string> | null => {
                if (debouncedSearchTerm.length === 0) {
                    return null
                }
                if (parsedSearch.mode !== 'search') {
                    return highlightedNodeIds(parsedSearch.baseName, parsedSearch.mode)
                }
                const lowerBaseName = parsedSearch.baseName.toLowerCase()
                return new Set(nodes.filter((n) => n.data.name.toLowerCase().includes(lowerBaseName)).map((n) => n.id))
            },
        ],
        filteredNodes: [
            (s) => [s.dataModelingNodes, s.searchTerm],
            (dataModelingNodes: DataModelingNode[], searchTerm: string): DataModelingNode[] => {
                if (!searchTerm) {
                    return dataModelingNodes
                }
                const { baseName } = parseSearchTerm(searchTerm)
                return dataModelingNodes.filter((n) => n.name.toLowerCase().includes(baseName.toLowerCase()))
            },
        ],
        selectedDag: [
            (s) => [s.dags, s.selectedDagId],
            (dags: DataModelingDAG[], selectedDagId: string | null): DataModelingDAG | null => {
                if (!selectedDagId) {
                    return null
                }
                return dags.find((d) => d.id === selectedDagId) ?? null
            },
        ],
        availableDagIds: [
            (s) => [s.filteredNodes],
            (nodes: DataModelingNode[]): string[] => {
                const viewableNodes = nodes.filter(
                    (n) => n.type === 'matview' || n.type === 'view' || n.type === 'endpoint'
                )
                return [...new Set(viewableNodes.map((n) => n.dag_name).filter(Boolean) as string[])].sort()
            },
        ],
        availableTypes: [
            (s) => [s.filteredNodes],
            (nodes: DataModelingNode[]): DataModelingNodeType[] => {
                const viewableNodes = nodes.filter(
                    (n) => n.type === 'matview' || n.type === 'view' || n.type === 'endpoint'
                )
                return [...new Set(viewableNodes.map((n) => n.type))].sort()
            },
        ],
        viewNodes: [
            (s) => [s.filteredNodes, s.filterDagIds, s.filterTypes],
            (
                nodes: DataModelingNode[],
                filterDagIds: string[],
                filterTypes: DataModelingNodeType[]
            ): DataModelingNode[] => {
                return nodes
                    .filter((n) => n.type === 'matview' || n.type === 'view' || n.type === 'endpoint')
                    .filter((n) => filterDagIds.length === 0 || (n.dag_name && filterDagIds.includes(n.dag_name)))
                    .filter((n) => filterTypes.length === 0 || filterTypes.includes(n.type))
            },
        ],
        visibleNodes: [
            (s) => [s.viewNodes, s.currentPage],
            (nodes: DataModelingNode[], currentPage: number): DataModelingNode[] => {
                const startIndex = (currentPage - 1) * PAGE_SIZE
                const endIndex = startIndex + PAGE_SIZE
                return nodes.slice(startIndex, endIndex)
            },
        ],
    }),
    listeners(({ values, actions, cache }) => ({
        setViewMode: ({ viewMode }) => {
            if (viewMode !== 'graph' && values.reactFlowInstance) {
                actions.setSavedViewport(values.reactFlowInstance.getViewport())
            }
            // graph view requires a single DAG — auto-select the first one
            if (viewMode === 'graph' && !values.selectedDagId && values.dags.length > 0) {
                actions.setSelectedDagId(values.dags[0].id)
            }
        },
        onEdgesChange: ({ edges }) => {
            actions.setEdges(applyEdgeChanges(edges, values.edges))
        },
        onNodesChange: ({ nodes }) => {
            // apply ReactFlow's dimension/position changes directly without re-running layout
            actions.setNodesRaw(applyNodeChanges(nodes, values.nodes))
        },
        loadDagsSuccess: ({ dags }) => {
            const isStale = values.selectedDagId && !dags.some((d) => d.id === values.selectedDagId)
            // graph view requires a single DAG — auto-select the first one, or reset stale persisted ID
            if (values.viewMode === 'graph' && (!values.selectedDagId || isStale) && dags.length > 0) {
                actions.setSelectedDagId(dags[0].id)
            } else if (isStale) {
                actions.setSelectedDagId(null)
            }
        },
        loadDataModelingNodesSuccess: () => {
            if (!values.dataModelingEdgesLoading) {
                actions.resetGraph(values.dataModelingNodes, values.dataModelingEdges)
            }
        },
        loadDataModelingEdgesSuccess: () => {
            if (!values.dataModelingNodesLoading) {
                actions.resetGraph(values.dataModelingNodes, values.dataModelingEdges)
            }
        },
        resetGraph: async ({ dataModelingNodes, dataModelingEdges, fitViewAfter }) => {
            const nodeIds = new Set(dataModelingNodes.map((n) => n.id))
            const isHorizontal = values.layoutDirection === 'RIGHT'

            const handlesByNodeId: Record<string, Record<string, NodeHandle>> = {}

            dataModelingNodes.forEach((node) => {
                handlesByNodeId[node.id] = {
                    [`target_${node.id}`]: {
                        id: `target_${node.id}`,
                        type: 'target',
                        position: isHorizontal ? Position.Left : Position.Top,
                    },
                    [`source_${node.id}`]: {
                        id: `source_${node.id}`,
                        type: 'source',
                        position: isHorizontal ? Position.Right : Position.Bottom,
                    },
                }
            })

            const sortedNodes = [...dataModelingNodes].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            )

            const nodes: Node[] = sortedNodes.map((node) => {
                return {
                    id: node.id,
                    type: 'model',
                    data: {
                        id: node.id,
                        name: node.name,
                        type: node.type,
                        dag: node.dag,
                        dagName: node.dag_name,
                        savedQueryId: node.saved_query_id,
                        handles: Object.values(handlesByNodeId[node.id] ?? {}),
                        upstreamCount: node.upstream_count,
                        downstreamCount: node.downstream_count,
                        userTag: node.user_tag,
                        lastJobStatus: node.last_run_status,
                        lastRunAt: node.last_run_at,
                        syncInterval: node.sync_interval,
                    },
                    position: { x: 0, y: 0 },
                    deletable: true,
                    selectable: true,
                    draggable: false,
                    connectable: false,
                }
            })

            const edges: Edge[] = dataModelingEdges
                .filter((edge) => nodeIds.has(edge.source_id) && nodeIds.has(edge.target_id))
                .map((edge) => ({
                    id: getEdgeId(edge.source_id, edge.target_id),
                    source: edge.source_id,
                    target: edge.target_id,
                    type: 'straight',
                    deletable: false,
                    markerEnd: { type: MarkerType.ArrowClosed },
                    sourceHandle: `source_${edge.source_id}`,
                    targetHandle: `target_${edge.target_id}`,
                }))

            actions.setEdges(edges)
            actions.setNodes(nodes, fitViewAfter)
        },
        setNodes: async ({ nodes, fitViewAfter }) => {
            if (nodes.length === 0) {
                actions.setNodesRaw([])
                actions.setGraphReady(true)
                return
            }
            const formattedNodes = await getFormattedNodes(nodes, values.edges, values.layoutDirection)
            actions.setNodesRaw(formattedNodes)
            if (values.debouncedSearchTerm.length > 0) {
                actions.layoutSearchResults()
            }
            if (fitViewAfter) {
                values.reactFlowInstance?.fitView({ padding: 0.2, maxZoom: 1 })
            }
            actions.setGraphReady(true)
        },
        layoutSearchResults: async (_, breakpoint) => {
            const matchedNodeIds = values.searchMatchedNodeIds
            if (matchedNodeIds === null) {
                actions.setSearchLayoutNodes(null)
                return
            }
            const matchedNodes = values.nodes.filter((node) => matchedNodeIds.has(node.id))
            const matchedEdges = values.edges.filter(
                (edge) => matchedNodeIds.has(edge.source) && matchedNodeIds.has(edge.target)
            )
            const formattedNodes = await getFormattedNodes(matchedNodes, matchedEdges, values.layoutDirection)
            await breakpoint()
            actions.setSearchLayoutNodes(formattedNodes)
        },
        setLayoutDirection: () => {
            if (values.dataModelingNodes.length > 0) {
                actions.resetGraph(values.dataModelingNodes, values.dataModelingEdges, true)
            }
        },
        setSearchTerm: async ({ searchTerm }, breakpoint) => {
            if (searchTerm.length > 0) {
                actions.setHighlightedNodeType(null)
            }
            await breakpoint(150)
            actions.setDebouncedSearchTerm(searchTerm)
        },
        setDebouncedSearchTerm: () => {
            actions.layoutSearchResults()
        },
        runNode: async ({ nodeId, direction }) => {
            const { upstream, downstream } = buildAdjacencyMaps(values.edges)
            const adjacency = direction === 'upstream' ? upstream : downstream
            const optimisticIds = traverseGraph(nodeId, adjacency)
            optimisticIds.add(nodeId)
            const now = Date.now()
            for (const id of optimisticIds) {
                if (!nodeStartTimes.has(id)) {
                    nodeStartTimes.set(id, now)
                }
            }
            actions.setRunningNodeIds(new Set([...values.runningNodeIds, ...optimisticIds]))

            try {
                const response = await api.dataModelingNodes.run(nodeId, direction)
                actions.runNodeSuccess(nodeId, direction, response.node_ids)
                actions.startPollingRunningJobs()
            } catch (e) {
                actions.runNodeFailure(nodeId, direction, String(e))
            }
        },
        materializeNode: async ({ nodeId }) => {
            try {
                await api.dataModelingNodes.materialize(nodeId)
                actions.materializeNodeSuccess(nodeId)
                actions.startPollingRunningJobs()
            } catch (e) {
                actions.materializeNodeFailure(nodeId, String(e))
            }
        },
        pollRunningJobs: async () => {
            actions.loadRecentJobs()
            try {
                const running = await api.dataModelingJobs.listRunning()
                actions.pollRunningJobsSuccess(running)
            } catch {
                // keep stale data during transient failures
            }
        },
        pollRunningJobsSuccess: ({ runningJobs }) => {
            const now = Date.now()
            const runningSavedQueryIds = new Set(runningJobs.map((job) => job.saved_query_id))
            const newRunningNodeIds = new Set<string>()

            for (const node of values.nodes) {
                if (node.data.savedQueryId && runningSavedQueryIds.has(node.data.savedQueryId)) {
                    newRunningNodeIds.add(node.id)
                }
            }
            for (const nodeId of values.runningNodeIds) {
                const startTime = nodeStartTimes.get(nodeId)
                if (startTime && now - startTime < MIN_RUNNING_DURATION_MS) {
                    newRunningNodeIds.add(nodeId)
                }
            }

            actions.setRunningNodeIds(newRunningNodeIds)
            if (newRunningNodeIds.size === 0) {
                actions.stopPollingRunningJobs()
            }
        },
        loadRecentJobs: async () => {
            try {
                const recent = await api.dataModelingJobs.listRecent()
                actions.loadRecentJobsSuccess(recent)
            } catch {
                // silent failure
            }
        },
        startPollingRunningJobs: () => {
            actions.pollRunningJobs()
            cache.disposables.add(() => {
                const intervalId = setInterval(() => {
                    actions.pollRunningJobs()
                }, POLL_INTERVAL_MS)
                return () => clearInterval(intervalId)
            }, 'pollRunningJobs')
        },

        stopPollingRunningJobs: () => {
            if (cache.disposables.dispose('pollRunningJobs')) {
                actions.loadRecentJobs()
            }
        },
        setSelectedDagId: () => {
            // clear stale graph immediately so old nodes don't flash
            actions.setNodesRaw([])
            actions.setEdges([])
            actions.loadDataModelingNodes()
            actions.loadDataModelingEdges()
        },
    })),
    urlToAction(({ actions, values }) => ({
        [urls.dataOps()]: (_, searchParams) => {
            if (typeof searchParams.dag === 'string' && searchParams.dag !== values.selectedDagId) {
                actions.setSelectedDagId(searchParams.dag)
            }
        },
    })),
    afterMount(({ actions }) => {
        actions.loadDags()
        actions.loadDataModelingNodes()
        actions.loadDataModelingEdges()
        actions.loadRecentJobs()
        actions.startPollingRunningJobs()
    }),
    beforeUnmount(({ actions }) => {
        actions.stopPollingRunningJobs()
    }),
])
